import React, { useContext, useMemo } from "react";
import { HolderOutlined } from "@ant-design/icons";
import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, Space, Table, Typography, Row, Col } from "antd";
import Meta from "../Meta";
import metaData from "../../data/metaData";
import moment from "moment"; // Import moment for date formatting
import deepcopy from "deepcopy";
import { formatTime, isWithin } from "../../utils/time";
import ProductTag from "../Table/ProductTag";

const { Text, Title } = Typography;
const RowContext = React.createContext({});

const DragHandle = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{
        cursor: "move",
      }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

const GiftRow = (props) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: props["data-row-key"],
  });
  const style = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging
      ? {
          position: "relative",
          zIndex: 9999,
        }
      : {}),
  };
  const contextValue = useMemo(
    () => ({
      setActivatorNodeRef,
      listeners,
    }),
    [setActivatorNodeRef, listeners]
  );

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

const GiftTable = ({
  state = {},
  periods = [],
  isLoading,
  readOnly = false,
  onEdit = () => {},
  onDelete = () => {},
  onUpdate = () => {},
}) => {
  const { data = [], onChange } = state;

  const onDragEnd = ({ active, over }) => {
    // Check if the 'over' is valid
    if (!over) {
      console.warn("Dropped outside valid area, ignoring.");
      return; // Ignore the drop if there's no valid target
    }

    if (active.id !== over.id) {
      let prevGifts = deepcopy(data);
      const activeIndex = prevGifts.findIndex((gift) => gift.id === active.id);
      const overIndex = prevGifts.findIndex((gift) => gift.id === over.id);
      const updatedArr = arrayMove(prevGifts, activeIndex, overIndex);

      // Return the updated array to the parent component
      onUpdate(updatedArr);
    }
  };

  const columns = [
    // Conditionally render the drag column based on readOnly
    ...(readOnly
      ? [] // Do not include the drag column if readOnly
      : [
          {
            title: "",
            dataIndex: "drag",
            width: 50,
            render: () => <DragHandle />,
          },
        ]),
    {
      title: "Gift",
      dataIndex: "product.name",
      key: "product.name",
      width: 400,
      render: (text, record) => {
        const product = record?.product || {};
        return (
          <Meta
            image={
              product.gallery?.[0] ? (
                <img
                  style={{ width: 75, objectFit: "cover", marginRight: "0.5rem" }}
                  src={`${metaData.blobPreURL}clientUpload-${product.gallery[0]}.jpeg${metaData.blobPostURL}`}
                  alt={product.name}
                />
              ) : null
            }
            title={<Title level={5}>{product?.name}</Title>}
            description={<ProductTag product={product} />}
            maxWidth={200}
          />
        );
      },
      sorter: (a, b) => (a?.product?.name || "").localeCompare(b?.product?.name || ""),
    },
    {
      title: "Redeem rule",
      dataIndex: "time.redeem",
      width: 350,
      render: (text, record) => {
        const redeemStart = record?.time?.redeem?.start;
        const redeemEnd = record?.time?.redeem?.end;

        const startDateFormatted = redeemStart ? formatTime(redeemStart, "YYYY-MM-DD") : "N/A";
        const endDateFormatted = redeemEnd ? formatTime(redeemEnd, "YYYY-MM-DD") : "N/A";

        // Use the existing isWithin function to check if it's ongoing
        const isOngoing = isWithin({ start: redeemStart, end: redeemEnd });

        // Define the button color based on status
        const buttonStyle = {
          backgroundColor: isOngoing ? "var(--ion-color-green-100)" : "var(--ion-color-orange-100)", // Green for ongoing, Orange for pending
          color: isOngoing ? "var(--ion-color-green-600)" : "var(--ion-color-orange-600)", // Green for ongoing, Orange for pending
          border: "none",
          borderRadius: "5px",
          padding: "0.2rem 0.5rem",
          cursor: "default",
        };

        return (
          <>
            <Row style={{ width: "100%", fontWeight: "bold" }}>
              {periods?.find((e) => e.id == record.periodId)?.name}
            </Row>
            <Text style={{ marginRight: "1rem" }}>{`${startDateFormatted} to ${endDateFormatted}`}</Text>
            <Button style={buttonStyle} disabled>
              {isOngoing ? "Ongoing" : "Pending"}
            </Button>
          </>
        );
      },
    },
    {
      title: "Qty",
      dataIndex: "qty",
      width: 100,
      render: (text, record) => {
        const qty = record?.qty || 0; // Default to 0 if undefined
        return <Text strong>{qty}</Text>;
      },
    },
    {
      title: "Require Coin",
      dataIndex: "cost",
      render: (text, record) => {
        const cost = record?.cost || 0; // Default to 0 if undefined
        return <Text strong>{cost}</Text>;
      },
    },
    // Conditionally render the Action column based on readOnly
    ...(readOnly
      ? [] // Do not include the action column if readOnly
      : [
          {
            title: "Action",
            width: 175,
            render: (_, record) => {
              const redeemStart = record?.time?.redeem?.start;
              const redeemEnd = record?.time?.redeem?.end;

              const isOngoing = isWithin({ start: redeemStart, end: redeemEnd });

              return (
                <Row justify="end">
                  <Space>
                    <Button onClick={() => onEdit(record?.id)}>Edit</Button>
                    <Button
                      style={{ visibility: isOngoing ? "hidden" : "display" }}
                      variant="text"
                      color="danger"
                      onClick={() => onDelete(record?.id)}
                    >
                      Delete
                    </Button>
                  </Space>
                </Row>
              );
            },
          },
        ]),
  ];

  return (
    <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
      <SortableContext items={data.map((gift) => gift.id)} strategy={verticalListSortingStrategy}>
        <Table
          rowKey="id"
          components={{
            body: {
              row: GiftRow,
            },
          }}
          columns={columns}
          loading={isLoading}
          dataSource={data}
          pagination={false} // Disable pagination for simplicity
          rowClassName="draggable-row" // Optional: Add class for styling
        />
      </SortableContext>
    </DndContext>
  );
};

export default GiftTable;
